<template>
  <v-container fluid grid-list-lg>

    <ConfirmModal
      v-model="showEditJsonConfirm"
      title="Edit JSON Directly"
      message="Are you sure you want to manually edit the JSON? Malformed JSON, misspelled keys, or unsupported Values may cause unexpected behaviors."
      @confirmed="showAdvancedCustom = true"
    ></ConfirmModal>
    <ConfirmModal
      v-model="showEditPermissionsJsonConfirm"
      title="Edit JSON Directly"
      message="Are you sure you want to manually edit the JSON? Malformed JSON, misspelled keys, or unsupported Values may cause unexpected behaviors."
      @confirmed="showAdvancedPermissions = true"
    ></ConfirmModal>
    <v-layout row wrap mb-2 class="grey lighten-4">
      <h2>
        <router-link to="/users">Users</router-link>
        <span class="grey--text">&gt;</span>
        <template v-if="!user.id">
          Add
        </template>
        <template v-else>
          <router-link :to="{ name: 'User', params: { id: $route.params.id } }">{{ user.name }} <span class="grey--text">(</span>{{ user.id }}<span class="grey--text">)</span></router-link>
          <span class="grey--text">&gt;</span> Edit
        </template>
      </h2>
    </v-layout>

    <v-form v-model="isFormValid" ref="form" @submit.prevent>
      <!-- Account Selection (for new users) -->
      <v-layout row wrap align-center>
        <v-flex xs12 sm6>
          <v-combobox
            name="account"
            label="Account"
            v-model="fields.account_id"
            :items="myProfile.accounts"
            item-text="name"
            item-value="account_id"
            box
            required
            :rules="[ v => !!v || 'Account must be selected' ]"
          />
        </v-flex>
        <v-flex xs12 sm3>
          <v-checkbox
            v-model="enableReportingOnCreate"
            label="Enable standard reporting"
            color="primary"
          ></v-checkbox>
        </v-flex>
        <v-flex xs12 sm3>
          <v-checkbox
            v-model="enableHppReceiptsOnCreate"
            label="Enable HPP receipts"
            color="primary"
            hint="Send HPP payment receipt copies to this user's email"
            persistent-hint
          ></v-checkbox>
        </v-flex>
      </v-layout>

      <!-- Basic Info -->
      <v-layout row wrap align-center>
        <h3 class="pa-2 primary--text">Basic Information</h3>
        <v-divider class="primary"></v-divider>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs12 sm6 lg4>
          <v-text-field
            label="Name"
            v-model="fields.name"
            box
            :class="hasFieldChanged('name') ? 'form__input--modified' : ''"
            required
            :rules="[ v => !!v || 'Name must be provided' ]"
          ></v-text-field>
        </v-flex>

        <v-flex xs12 sm6 lg4>
          <v-text-field
            label="Email"
            v-model="fields.email"
            box
            :class="hasFieldChanged('email') ? 'form__input--modified' : ''"
            required
            :rules="[ v => !!v || 'Email must be provided', rules.isEmailValid ]"
          ></v-text-field>
        </v-flex>

        <v-flex xs12 sm6 lg4>
          <v-combobox
            label="Position"
            v-model="fields.position"
            :items="['User', 'Owner', 'Admin', 'Agent', 'Account Rep']"
            box
            :class="hasFieldChanged('position') ? 'form__input--modified' : ''"
            :rules="[ v => !!v || 'Position must be provided' ]"
          ></v-combobox>
        </v-flex>

        <v-flex xs12 sm8 lg6>
          <v-layout row align-center>
            <v-flex>
              <v-text-field
                label="Password"
                v-model="fields.password"
                box
                :class="hasFieldChanged('password') ? 'form__input--modified' : ''"
                :type="showPassword ? 'text' : 'password'"
                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                @click:append="showPassword = !showPassword"
                :hint="user.id ? 'Leave blank to keep current password' : 'Min 8 chars, uppercase, lowercase, number'"
                persistent-hint
                :rules="[ v => !!user.id || !!v || 'Password must be provided' ]"
              ></v-text-field>
            </v-flex>
            <v-flex shrink>
              <v-btn small outline color="primary" @click="generateKsuidPassword">Generate Password</v-btn>
            </v-flex>
          </v-layout>
        </v-flex>

      </v-layout>

      <!-- Permissions & Custom Settings Section -->
      <v-layout row wrap align-center>
        <h3 class="pa-2 primary--text">Permissions &amp; Custom Settings</h3>
        <v-divider class="primary"></v-divider>
      </v-layout>
      <v-layout row wrap mb-2>
        <v-flex xs12>
          <p class="caption grey--text mb-2">Selecting a preset will overwrite both Permissions and Custom Settings.</p>
          <v-radio-group v-model="selectedPreset" row hide-details @change="onPresetChange" class="mt-0">
            <v-radio label="Standard User" value="standard" color="primary"></v-radio>
            <v-radio label="BlueYonder User" value="blueyonder" color="primary"></v-radio>
            <v-radio label="Admin User" value="admin" color="primary"></v-radio>
            <v-radio label="Copy Owner" value="owner" color="primary" :disabled="!selectedAccountId || loadingOwnerPerms"></v-radio>
          </v-radio-group>
          <v-btn small outline color="primary" class="mt-2" @click="applyPreset('clear'); selectedPreset = null">Clear All</v-btn>
        </v-flex>
      </v-layout>

      <!-- Permissions Panel -->
      <v-layout row wrap align-center mt-2>
        <h3 class="pa-2 primary--text">Permissions</h3>
        <v-divider class="primary"></v-divider>
      </v-layout>

          <!-- Simple Permissions View -->
          <v-layout row wrap class="pa-2" v-if="!showAdvancedPermissions">
            <v-flex xs12>
              <v-data-table :headers="columnTableHeaders" :items="activePermissions" hide-actions class="border">
                <template v-slot:headers="props">
                  <tr>
                    <th v-for="header in props.headers" :key="header.value" class="primary white--text subheading" :class="header.align ? `text-xs-${header.align}` : ''">{{ header.text }}</th>
                  </tr>
                </template>
                <template v-slot:items="props">
                  <td>{{ props.item.label }}</td>
                  <td>{{ props.item.key }}</td>
                  <td><v-chip small color="success" text-color="white">Enabled</v-chip></td>
                  <td class="text-xs-right">
                    <v-btn flat small color="error" @click="togglePermission(props.item.key, false)"><v-icon small left>fa-trash</v-icon> Delete</v-btn>
                  </td>
                </template>
                <template v-slot:no-data><span class="grey--text pa-2">No permissions enabled</span></template>
              </v-data-table>
              <v-layout row align-center mt-2>
                <v-flex>
                  <v-select v-model="pendingPermission" :items="remainingPermissions" item-text="label" item-value="key" label="Add Permission" clearable hide-details box>
                    <template v-slot:item="data">
                      <v-list-tile-content>
                        <v-list-tile-title>{{ data.item.label }}</v-list-tile-title>
                        <v-list-tile-sub-title>{{ data.item.key }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </template>
                  </v-select>
                </v-flex>
                <v-flex shrink>
                  <v-btn small color="primary" :disabled="!pendingPermission" @click="togglePermission(pendingPermission, true); pendingPermission = null"><v-icon small>fa-plus</v-icon></v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs12 mt-3>
              <v-layout row align-center>
                <h4 class="pa-2 grey--text text--darken-2">Preview</h4>
                <v-btn small outline color="primary" @click="confirmEditPermissionsJson">Edit JSON Directly</v-btn>
              </v-layout>
              <pre class="custom-json-preview">{{ JSON.stringify(permissionsObj, null, 2) }}</pre>
            </v-flex>
          </v-layout>

          <!-- Advanced Permissions View (JSON) -->
          <v-layout row wrap class="pa-2" v-else>
            <v-flex xs12>
              <v-layout row align-center mb-1>
                <h4 class="pa-2 grey--text text--darken-2">Permissions (JSON)</h4>
                <v-btn small outline color="primary" @click="showAdvancedPermissions = false">Simple View</v-btn>
              </v-layout>
              <v-textarea
                v-model="fields.permissions"
                box
                :class="hasFieldChanged('permissions') ? 'form__input--modified' : ''"
                :rules="[ rules.validJson, rules.validPermissionsKeys ]"
                rows="16"
                auto-grow
              ></v-textarea>
            </v-flex>
          </v-layout>
      <!-- Custom Settings Panel -->
      <v-layout row wrap align-center mt-4>
        <h3 class="pa-2 primary--text">Custom Settings</h3>
        <v-divider class="primary"></v-divider>
      </v-layout>

          <!-- Simple Custom Settings View -->
          <v-layout row wrap class="pa-2" v-if="!showAdvancedCustom">
            <v-flex xs12>
              <h3 class="pa-2 primary--text headline">Custom Pagination Setting</h3>
            </v-flex>
            <v-flex xs12 mb-3>
              <v-text-field
                label="Rows Per Page"
                style="max-width: 200px"
                :value="customObj.pagination && customObj.pagination.rowsPerPage"
                @input="setCustomRowsPerPage($event)"
                type="number"
                hide-details
                box
              ></v-text-field>
            </v-flex>
            <v-flex xs12 mt-3>
              <h3 class="pa-2 primary--text headline">Custom Card Settings</h3>
            </v-flex>
            <v-layout row wrap class="border pa-3">
              <v-flex xs12 sm6 pa-3>
                <h4 class="pa-2 grey--text text--darken-2 title">Card Transaction Columns</h4>
                <v-data-table :headers="columnTableHeaders" :items="activeColumnsFor('transaction_columns')" hide-actions class="border">
                  <template v-slot:headers="props">
                    <tr>
                      <th v-for="header in props.headers" :key="header.value" class="primary white--text subheading" :class="header.align ? `text-xs-${header.align}` : ''">{{ header.text }}</th>
                    </tr>
                  </template>
                  <template v-slot:items="props">
                    <td>{{ props.item.label }}</td>
                    <td>{{ props.item.value }}</td>
                    <td><v-chip small color="success" text-color="white">Enabled</v-chip></td>
                    <td class="text-xs-right">
                      <v-btn flat small color="error" @click="removeColumnFromField('transaction_columns', props.item.value)"><v-icon small left>fa-trash</v-icon> Delete</v-btn>
                    </td>
                  </template>
                  <template v-slot:no-data><span class="grey--text pa-2">No columns selected</span></template>
                </v-data-table>
                <v-layout row align-center mt-2>
                  <v-flex>
                    <v-select v-model="pendingColumn.transaction_columns" :items="remainingColumnsFor('transaction_columns')" item-text="label" item-value="value" label="Add Card Transaction Column" clearable hide-details box>
                      <template v-slot:item="data">
                        <v-list-tile-content>
                          <v-list-tile-title>{{ data.item.label }}</v-list-tile-title>
                          <v-list-tile-sub-title>{{ data.item.value }}</v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                    </v-select>
                  </v-flex>
                  <v-flex shrink>
                    <v-btn small color="primary" :disabled="!pendingColumn.transaction_columns" @click="addColumnToField('transaction_columns')"><v-icon small>fa-plus</v-icon></v-btn>
                  </v-flex>
                </v-layout>
              </v-flex>

              <v-flex xs12 sm6 pa-3>
                <h4 class="pa-2 grey--text text--darken-2 title">Card Transaction Export Columns</h4>
                <v-data-table :headers="columnTableHeaders" :items="activeColumnsFor('transaction_export_columns')" hide-actions class="border">
                  <template v-slot:headers="props">
                    <tr>
                      <th v-for="header in props.headers" :key="header.value" class="primary white--text subheading" :class="header.align ? `text-xs-${header.align}` : ''">{{ header.text }}</th>
                    </tr>
                  </template>
                  <template v-slot:items="props">
                    <td>{{ props.item.label }}</td>
                    <td>{{ props.item.value }}</td>
                    <td><v-chip small color="success" text-color="white">Enabled</v-chip></td>
                    <td class="text-xs-right">
                      <v-btn flat small color="error" @click="removeColumnFromField('transaction_export_columns', props.item.value)"><v-icon small left>fa-trash</v-icon> Delete</v-btn>
                    </td>
                  </template>
                  <template v-slot:no-data><span class="grey--text pa-2">No columns selected</span></template>
                </v-data-table>
                <v-layout row align-center mt-2>
                  <v-flex>
                    <v-select v-model="pendingColumn.transaction_export_columns" :items="remainingColumnsFor('transaction_export_columns')" item-text="label" item-value="value" label="Add Card Transaction Export Column" clearable hide-details box>
                      <template v-slot:item="data">
                        <v-list-tile-content>
                          <v-list-tile-title>{{ data.item.label }}</v-list-tile-title>
                          <v-list-tile-sub-title>{{ data.item.value }}</v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                    </v-select>
                  </v-flex>
                  <v-flex shrink>
                    <v-btn small color="primary" :disabled="!pendingColumn.transaction_export_columns" @click="addColumnToField('transaction_export_columns')"><v-icon small>fa-plus</v-icon></v-btn>
                  </v-flex>
                </v-layout>
              </v-flex>

            </v-layout>
            <v-flex xs12 mt-3>
              <h3 class="pa-2 primary--text headline">Custom ACH settings</h3>
            </v-flex>
            <v-layout row wrap class="border pa-3">
              <v-flex xs12 sm6 pa-3>
                <h4 class="pa-2 grey--text text--darken-2 title">ACH Transaction Columns</h4>
                <v-data-table :headers="columnTableHeaders" :items="activeColumnsFor('transaction_ach_columns')" hide-actions class="border">
                  <template v-slot:headers="props">
                    <tr>
                      <th v-for="header in props.headers" :key="header.value" class="primary white--text subheading" :class="header.align ? `text-xs-${header.align}` : ''">{{ header.text }}</th>
                    </tr>
                  </template>
                  <template v-slot:items="props">
                    <td>{{ props.item.label }}</td>
                    <td>{{ props.item.value }}</td>
                    <td><v-chip small color="success" text-color="white">Enabled</v-chip></td>
                    <td class="text-xs-right">
                      <v-btn flat small color="error" @click="removeColumnFromField('transaction_ach_columns', props.item.value)"><v-icon small left>fa-trash</v-icon> Delete</v-btn>
                    </td>
                  </template>
                  <template v-slot:no-data><span class="grey--text pa-2">No columns selected</span></template>
                </v-data-table>
                <v-layout row align-center mt-2>
                  <v-flex>
                    <v-select v-model="pendingColumn.transaction_ach_columns" :items="remainingColumnsFor('transaction_ach_columns')" item-text="label" item-value="value" label="Add ACH Transaction Column" clearable hide-details box>
                      <template v-slot:item="data">
                        <v-list-tile-content>
                          <v-list-tile-title>{{ data.item.label }}</v-list-tile-title>
                          <v-list-tile-sub-title>{{ data.item.value }}</v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                    </v-select>
                  </v-flex>
                  <v-flex shrink>
                    <v-btn small color="primary" :disabled="!pendingColumn.transaction_ach_columns" @click="addColumnToField('transaction_ach_columns')"><v-icon small>fa-plus</v-icon></v-btn>
                  </v-flex>
                </v-layout>
              </v-flex>

              <v-flex xs12 sm6 pa-3>
                <h4 class="pa-2 grey--text text--darken-2 title">ACH Transaction Export Columns</h4>
                <v-data-table :headers="columnTableHeaders" :items="activeColumnsFor('transaction_ach_export_columns')" hide-actions class="border">
                  <template v-slot:headers="props">
                    <tr>
                      <th v-for="header in props.headers" :key="header.value" class="primary white--text subheading" :class="header.align ? `text-xs-${header.align}` : ''">{{ header.text }}</th>
                    </tr>
                  </template>
                  <template v-slot:items="props">
                    <td>{{ props.item.label }}</td>
                    <td>{{ props.item.value }}</td>
                    <td><v-chip small color="success" text-color="white">Enabled</v-chip></td>
                    <td class="text-xs-right">
                      <v-btn flat small color="error" @click="removeColumnFromField('transaction_ach_export_columns', props.item.value)"><v-icon small left>fa-trash</v-icon> Delete</v-btn>
                    </td>
                  </template>
                  <template v-slot:no-data><span class="grey--text pa-2">No columns selected</span></template>
                </v-data-table>
                <v-layout row align-center mt-2>
                  <v-flex>
                    <v-select v-model="pendingColumn.transaction_ach_export_columns" :items="remainingColumnsFor('transaction_ach_export_columns')" item-text="label" item-value="value" label="Add ACH Transaction Export Column" clearable hide-details box>
                      <template v-slot:item="data">
                        <v-list-tile-content>
                          <v-list-tile-title>{{ data.item.label }}</v-list-tile-title>
                          <v-list-tile-sub-title>{{ data.item.value }}</v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                    </v-select>
                  </v-flex>
                  <v-flex shrink>
                    <v-btn small color="primary" :disabled="!pendingColumn.transaction_ach_export_columns" @click="addColumnToField('transaction_ach_export_columns')"><v-icon small>fa-plus</v-icon></v-btn>
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
            <v-flex xs12 mt-3>
              <v-layout row align-center>
                <h4 class="pa-2 grey--text text--darken-2">Preview</h4>
                <v-btn small outline color="primary" @click="confirmEditJson">Edit JSON Directly</v-btn>
              </v-layout>
              <pre class="custom-json-preview">{{ JSON.stringify(customObj, null, 2) }}</pre>
            </v-flex>
          </v-layout>

          <!-- Advanced Custom Settings View (JSON) -->
          <v-layout row wrap class="pa-2" v-else>
            <v-flex xs12>
              <v-layout row align-center mb-1>
                <h4 class="pa-2 grey--text text--darken-2">Custom Settings (JSON)</h4>
                <v-btn small outline color="primary" @click="showAdvancedCustom = false">Simple View</v-btn>
              </v-layout>
              <v-textarea
                v-model="fields.custom"
                box
                :class="hasFieldChanged('custom') ? 'form__input--modified' : ''"
                :rules="[ rules.validJson, rules.validCustomKeys ]"
                rows="12"
                auto-grow
              ></v-textarea>
            </v-flex>
          </v-layout>
      <v-divider class="primary mt-4"></v-divider>

      <v-layout row wrap>
        <v-flex xs12>
          <v-layout row wrap justify-end>
            <v-btn color="primary" outline @click.stop="reset" :disabled="!submitDisabled">reset</v-btn>
            <v-btn color="primary" @click.stop="save" :disabled="!submitDisabled" :loading="isProcessing">save</v-btn>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-form>
  </v-container>
</template>

<script>
import KSUID from 'ksuid'
import Bus from '@/bus'

import APIMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import DisplayMixin from '@/mixins/display'
import StoreMixin from '@/store/mixin'
import UtilityMixin from '@/mixins/utility'
import ConfirmModal from '@/components/ConfirmModal'

const ALLOWED_USER_PERMISSIONS = new Set([
  'superuser',
  'can_manage_users',
  'can_manage_accounts',
  'can_manage_terminals',
  'can_manage_processors',
  'can_use_virtual_terminal',
  'can_standalone_refund',
  'can_view_transactions',
  'can_manage_transactions',
  'can_view_account_transactions',
  'can_manage_account_transactions',
  'can_view_schedules',
  'can_manage_schedules',
  'can_view_account_schedules',
  'can_manage_account_schedules',
  'can_waive_card_fees',
  'can_waive_health_card_fees',
  'can_refund_ach_without_wait',
  'can_view_funding',
  'can_use_jetpay',
  'can_use_okdmv_renewal',
  'has_installment_options',
  'has_recurring_options',
  'has_schedules'
])

const ALLOWED_CUSTOM_KEYS = new Set([
  'default_account_id',
  'default_card_terminal_id',
  'default_ach_terminal_id',
  'pagination',
  'transaction_columns',
  'transaction_export_columns',
  'transaction_ach_columns',
  'transaction_ach_export_columns',
  'password_grace_period'
])

const ALLOWED_COLUMN_VALUES = new Set([
  'tracking_id',
  'reference_number',
  'customer_name',
  'billing_email',
  'request_type',
  'transactions_source_id',
  'transaction_status',
  'last_four',
  'card_expiration_yymm',
  'payment_type',
  'total_amount',
  'net_amount',
  'less_fee_amount',
  'fee_amount',
  'is_card_present',
  'is_recurring',
  'is_fee_waived',
  'ach_is_authorized',
  'order_number',
  'customer_po',
  'custom_field_1',
  'custom_field_2',
  'custom_field_3',
  'account_name',
  'terminal_name',
  'terminal_code',
  'user_name',
  'user_email',
  'response_code',
  'response_text',
  'ach_rejected',
  'created',
  'settled'
])

export default {
  components: {
    ConfirmModal
  },

  mixins: [
    APIMixin,
    AuthMixin,
    DisplayMixin,
    StoreMixin,
    UtilityMixin
  ],

  data: function () {
    return {
      user: {},
      isProcessing: false,
      isFormValid: false,
      showPassword: false,
      selectedPreset: null,
      showAdvancedPermissions: false,
      showAdvancedCustom: false,
      showEditJsonConfirm: false,
      showEditPermissionsJsonConfirm: false,
      pendingPermission: null,
      pendingColumn: {
        transaction_columns: null,
        transaction_export_columns: null,
        transaction_ach_columns: null,
        transaction_ach_export_columns: null
      },
      columnTableHeaders: [
        { text: 'Setting', value: 'label', sortable: false },
        { text: 'Setting Key', value: 'key_value', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Action', value: 'actions', sortable: false, align: 'right' }
      ],
      transactionColumnsList: [
        { value: 'tracking_id', label: 'ID' },
        { value: 'reference_number', label: 'Reference ID' },
        { value: 'customer_name', label: 'Card/Acct. Name' },
        { value: 'billing_email', label: 'Cust. Email' },
        { value: 'request_type', label: 'Type' },
        { value: 'transactions_source_id', label: 'Hosted Payment' },
        { value: 'transaction_status', label: 'Status' },
        { value: 'last_four', label: 'Last Four' },
        { value: 'card_expiration_yymm', label: 'Expiration' },
        { value: 'payment_type', label: 'Card Type' },
        { value: 'total_amount', label: 'Amount ($)' },
        { value: 'net_amount', label: 'Net ($)' },
        { value: 'less_fee_amount', label: 'Less Adj ($)' },
        { value: 'fee_amount', label: 'Fee ($)' },
        { value: 'is_card_present', label: 'Card Present' },
        { value: 'is_recurring', label: 'Recurring' },
        { value: 'is_fee_waived', label: 'Fee Waived' },
        { value: 'ach_is_authorized', label: 'Authorized' },
        { value: 'order_number', label: 'Order Number' },
        { value: 'customer_po', label: 'Customer PO' },
        { value: 'custom_field_1', label: 'Custom 1' },
        { value: 'custom_field_2', label: 'Custom 2' },
        { value: 'custom_field_3', label: 'Custom 3' },
        { value: 'account_name', label: 'Account' },
        { value: 'terminal_name', label: 'Terminal Name' },
        { value: 'terminal_code', label: 'Terminal Code' },
        { value: 'user_name', label: 'Created By' },
        { value: 'user_email', label: 'User Email' },
        { value: 'response_code', label: 'Response Code' },
        { value: 'response_text', label: 'Response Text' },
        { value: 'ach_rejected', label: 'Rejected' },
        { value: 'created', label: 'Created' },
        { value: 'settled', label: 'Settled' }
      ],
      enableReportingOnCreate: false,
      enableHppReceiptsOnCreate: false,
      loadingOwnerPerms: false,
      fields: this.defaultFormFields(),

      // Permission definitions for checkbox grid
      permissionsList: [
        { key: 'superuser', label: 'Superuser (Full Access)' },
        { key: 'can_manage_users', label: 'Manage Users' },
        { key: 'can_use_virtual_terminal', label: 'Use Virtual Terminal' },
        { key: 'can_view_transactions', label: 'View Transactions' },
        { key: 'can_manage_transactions', label: 'Manage Transactions' },
        { key: 'can_view_account_transactions', label: 'View Account Transactions' },
        { key: 'can_manage_account_transactions', label: 'Manage Account Transactions' },
        { key: 'can_view_schedules', label: 'View Schedules' },
        { key: 'can_manage_schedules', label: 'Manage Schedules' },
        { key: 'can_view_account_schedules', label: 'View Account Schedules' },
        { key: 'can_manage_account_schedules', label: 'Manage Account Schedules' },
        { key: 'can_waive_card_fees', label: 'Waive Card Fees' },
        { key: 'can_waive_health_card_fees', label: 'Waive Health Card Fees' },
        { key: 'has_installment_options', label: 'Installment Options' },
        { key: 'has_recurring_options', label: 'Recurring Options' },
        { key: 'has_schedules', label: 'Recurring Schedules' },
        { key: 'can_refund_ach_without_wait', label: 'Refund ACH Without Wait' }
      ],

      // Permission presets
      presets: {
        standard: {
          can_use_virtual_terminal: true,
          can_view_transactions: true,
          can_manage_transactions: true,
          can_view_account_transactions: true,
          can_manage_account_transactions: true,
          can_view_schedules: true,
          can_manage_schedules: true,
          can_view_account_schedules: true,
          can_manage_account_schedules: true,
          can_waive_card_fees: true,
          has_schedules: true
        },
        blueyonder: {
          can_use_virtual_terminal: true,
          can_view_transactions: true,
          can_manage_transactions: true,
          can_view_account_transactions: true,
          can_manage_account_transactions: true,
          can_view_schedules: true,
          can_manage_schedules: true,
          can_view_account_schedules: true,
          can_manage_account_schedules: true,
          can_waive_card_fees: true,
          can_waive_health_card_fees: true,
          has_installment_options: true,
          has_recurring_options: true,
          has_schedules: true
        },
        admin: {
          can_use_virtual_terminal: true,
          can_view_transactions: true,
          can_manage_transactions: true,
          can_view_account_transactions: true,
          can_manage_account_transactions: true,
          can_view_schedules: true,
          can_manage_schedules: true,
          can_view_account_schedules: true,
          can_manage_account_schedules: true,
          can_waive_card_fees: true,
          can_waive_health_card_fees: true,
          has_installment_options: true,
          has_recurring_options: true,
          has_schedules: true,
          can_refund_ach_without_wait: true
        },
        clear: {}
      },

      rules: {
        required: value => !!value || 'Value is required',
        isEmailValid: value => /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value) || 'Invalid email address',
        validJson: value => this.isJsonValid(value, true) || 'Invalid JSON',
        validPermissionsKeys: value => {
          try {
            const str = typeof value === 'string' ? value : JSON.stringify(value)

            // Detect duplicate keys before parsing (JSON.parse silently drops them)
            const keyRegex = /"([^"]+)"\s*:/g
            const keys = []
            let match
            while ((match = keyRegex.exec(str)) !== null) {
              keys.push(match[1])
            }
            if (new Set(keys).size !== keys.length) return 'Duplicate keys are not allowed'

            const obj = JSON.parse(str)
            if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return true
            const hasInvalidKey = Object.keys(obj).some(k => !ALLOWED_USER_PERMISSIONS.has(k))
            const hasInvalidValue = Object.values(obj).some(v => typeof v !== 'boolean')
            return (!hasInvalidKey && !hasInvalidValue) || 'Invalid key or value'
          } catch (e) {
            return true // JSON parse errors are caught by the validJson rule
          }
        },
        validCustomKeys: value => {
          try {
            const str = typeof value === 'string' ? value : JSON.stringify(value)

            // Detect duplicate keys before parsing (JSON.parse silently drops them)
            const keyRegex = /"([^"]+)"\s*:/g
            const keys = []
            let match
            while ((match = keyRegex.exec(str)) !== null) {
              keys.push(match[1])
            }
            if (new Set(keys).size !== keys.length) return 'Duplicate keys are not allowed'

            const obj = JSON.parse(str)
            if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return true
            const hasInvalidKey = Object.keys(obj).some(k => !ALLOWED_CUSTOM_KEYS.has(k))
            if (hasInvalidKey) return 'Invalid key or value'
            const columnFields = [
              'transaction_columns',
              'transaction_export_columns',
              'transaction_ach_columns',
              'transaction_ach_export_columns'
            ]
            for (const field of columnFields) {
              if (Array.isArray(obj[field]) && obj[field].some(v => !ALLOWED_COLUMN_VALUES.has(v))) {
                return 'Invalid key or value'
              }
            }
            return true
          } catch (e) {
            return true // JSON parse errors are caught by the validJson rule
          }
        },
      }
    }
  },

  computed: {
    environment () {
      return process.env.NODE_ENV
    },

    accounts: function () {
      let accounts = [
        { account_id: '*', name: 'All' }
      ]
      return accounts.concat(this.myProfile.accounts)
    },

    selectedAccountId () {
      let acct = this.fields.account_id
      if (typeof acct === 'object' && acct !== null) {
        return acct.account_id || acct.id
      }
      return acct
    },

    customObj () {
      try {
        if (typeof this.fields.custom === 'string') {
          return JSON.parse(this.fields.custom) || {}
        }
        return this.fields.custom || {}
      } catch (e) {
        return {}
      }
    },

    activePermissions () {
      return this.permissionsList.filter(p => this.permissionsObj[p.key])
    },

    remainingPermissions () {
      return this.permissionsList.filter(p => !this.permissionsObj[p.key])
    },

    // Parse permissions JSON into object for checkbox binding
    permissionsObj () {
      try {
        if (typeof this.fields.permissions === 'string') {
          return JSON.parse(this.fields.permissions) || {}
        }
        return this.fields.permissions || {}
      } catch (e) {
        return {}
      }
    },

    submitDisabled () {
      if (!this.isFormValid) {
        return true
      }

      if (!this.user.id) {
        return true
      }

      // only add fields that have changed
      for (const field in this.fields) {
        if (this.hasFieldChanged(field)) {
          return true
        }
      }

      return false
    }
  },

  mounted () {
    this.load()
  },

  beforeRouteUpdate (to, from, next) {
    this.reset()
    next()
  },

  watch: {
    $route (to, from) {
      this.reset()
    }
  },

  methods: {
    defaultFormFields () {
      return {
        id: 0,
        account_id: null,
        name: '',
        email: '',
        password: '',
        position: '',
        permissions: '{}',
        custom: JSON.stringify({
          pagination: {
            rowsPerPage: 10
          },
          transaction_columns: [
            'customer_name',
            'request_type',
            'transaction_status',
            'transactions_source_id',
            'last_four',
            'card_expiration_yymm',
            'total_amount',
            'less_fee_amount',
            'is_recurring',
            'custom_field_1',
            'created',
            'settled'
          ],
          transaction_export_columns: [
            'customer_name',
            'request_type',
            'transaction_status',
            'transactions_source_id',
            'last_four',
            'card_expiration_yymm',
            'total_amount',
            'less_fee_amount',
            'is_recurring',
            'customer_po',
            'custom_field_1',
            'custom_field_2',
            'custom_field_3',
            'created',
            'settled'
          ]
        }, null, 2),
        enabled: true
      }
    },

    reset () {
      this.fields = this.defaultFormFields()
      this.showAdvancedPermissions = false
      this.showAdvancedCustom = false
      this.enableReportingOnCreate = false
      this.enableHppReceiptsOnCreate = false
      this.load()
    },

    togglePermission (key, value) {
      let perms = { ...this.permissionsObj }
      if (value) {
        perms[key] = true
      } else {
        delete perms[key]
      }
      this.fields.permissions = JSON.stringify(perms, null, 2)
    },

    onPresetChange (value) {
      if (value === 'owner') {
        this.copyOwnerPermissions()
      } else {
        this.applyPreset(value)
      }
    },

    applyPreset (presetName) {
      const preset = this.presets[presetName]
      this.fields.permissions = JSON.stringify(preset, null, 2)
      this.selectedPreset = presetName
    },

    async copyOwnerPermissions () {
      if (!this.selectedAccountId) {
        Bus.$emit('openModal', { status: 'error', message: 'Please select an account first' })
        return
      }

      this.loadingOwnerPerms = true
      try {
        let result = await this.api({
          method: 'get',
          url: `/users/accounts/${this.selectedAccountId}/owner-permissions`
        })
        this.fields.permissions = JSON.stringify(result.data.permissions, null, 2)
        this.selectedPreset = 'owner'
        Bus.$emit('openModal', {
          status: 'success',
          message: `Copied permissions from owner: ${result.data.owner_name}`
        })
      } catch (error) {
        Bus.$emit('openModal', {
          status: 'error',
          message: (error.response && error.response.data && error.response.data.error) || 'No owner found for this account'
        })
      } finally {
        this.loadingOwnerPerms = false
      }
    },

    hasFieldChanged (field) {
      if (!this.user.id) {
        return false
      }
      if (field === 'custom' || field === 'permissions') {
        if (this.fields[field] !== this.objectPretty(this.user[field])) {
          return true
        }
      } else if (this.fields[field] !== this.user[field]) {
        return true
      }

      return false
    },

    generateKsuidPassword () {
      this.fields.password = KSUID.randomSync().string
    },

    confirmEditJson () {
      this.showEditJsonConfirm = true
    },

    confirmEditPermissionsJson () {
      this.showEditPermissionsJsonConfirm = true
    },

    toggleColumn (currentCols, column, checked) {
      let cols = Array.isArray(currentCols) ? [...currentCols] : []
      if (checked) {
        if (!cols.includes(column)) cols.push(column)
      } else {
        cols = cols.filter(c => c !== column)
      }
      return cols
    },

    setCustomColumns (key, value) {
      let custom = { ...this.customObj }
      custom[key] = value
      this.fields.custom = JSON.stringify(custom, null, 2)
    },

    activeColumnsFor (field) {
      const active = this.customObj[field] || []
      return this.transactionColumnsList.filter(col => active.includes(col.value))
    },

    remainingColumnsFor (field) {
      const active = this.customObj[field] || []
      return this.transactionColumnsList.filter(col => !active.includes(col.value))
    },

    addColumnToField (field) {
      const value = this.pendingColumn[field]
      if (!value) return
      const current = this.customObj[field] || []
      this.setCustomColumns(field, [...current, value])
      this.pendingColumn[field] = null
    },

    removeColumnFromField (field, value) {
      this.setCustomColumns(field, (this.customObj[field] || []).filter(v => v !== value))
    },

    setCustomRowsPerPage (value) {
      let custom = { ...this.customObj }
      if (!custom.pagination) custom.pagination = {}
      custom.pagination.rowsPerPage = parseInt(value) || 10
      this.fields.custom = JSON.stringify(custom, null, 2)
    },

    async load () {
      try {
        Bus.$emit('openWaitModal')

        // Support account_id from route param (/users/add/:account_id) or query string (?account_id=)
        if (!this.$route.params.id) {
          const accountId = this.$route.params.account_id || this.$route.query.account_id
          if (accountId) {
            this.fields.account_id = parseInt(accountId) || accountId
          }
        }

        if (!this.$route.params.id) {
          // New user - apply standard preset by default
          this.applyPreset('standard')
          Bus.$emit('closeWaitModal')
          return
        }

        let data = await this.api({ url: `/users/${this.$route.params.id}` })

        this.user = data.data.user
        this.user.password = ''

        for (const [key, value] of Object.entries(data.data.user)) {
          if (this.fields.hasOwnProperty(key)) {
            if (key === 'permissions' || key === 'custom') {
              this.fields[key] = this.objectPretty(value)
            } else {
              this.fields[key] = value
            }
          }
        }

        if (data.data.user.accounts && data.data.user.accounts.length) {
          const userAccountId = data.data.user.accounts[0].id
          const matchedAccount = (this.myProfile.accounts || []).find(a => a.account_id === userAccountId)
          this.fields.account_id = matchedAccount || userAccountId
        }
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        Bus.$emit('closeWaitModal')
      }

      if (this.$refs.form) {
        this.$refs.form.validate()
      }
    },

    async save () {
      this.isProcessing = true

      try {
        if (!this.$refs.form.validate()) {
          let errors = []
          this.$refs.form.inputs.forEach(e => {
            if (e.hasError) {
              errors.push(e.errorBucket[0])
            }
          })

          let message = 'Validation Error'

          if (errors.length > 1) {
            message = 'Validation Errors'
          }

          Bus.$emit('openModal', {
            status: 'error',
            message: message,
            messageDetails: errors
          })

          return false
        }

        let options = { ...this.fields }

        // Parse custom JSON
        try {
          options.custom = JSON.parse(options.custom)
        } catch (e) {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Validation Error',
            messageDetails: 'Invalid JSON for custom field'
          })
          return false
        }

        // Parse permissions JSON
        try {
          options.permissions = JSON.parse(options.permissions)
        } catch (e) {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Validation Error',
            messageDetails: 'Invalid JSON for permissions field'
          })
          return false
        }

        // Remove password if empty (for updates)
        if (this.user.id && !options.password) {
          delete options.password
        }

        // Normalize name and email before submitting
        if (options.name) options.name = options.name.trim().replace(/\s\s+/g, ' ')
        if (options.email) options.email = options.email.trim().toLowerCase()

        Bus.$emit('openWaitModal')

        // For new users, use the setup endpoint if account is selected
        let accountId = options.account_id
        if (typeof accountId === 'object' && accountId !== null) {
          accountId = accountId.account_id || accountId.id
        }

        if (!this.user.id && accountId) {
          let result = await this.api({
            method: 'post',
            url: '/users/setup',
            data: {
              name: options.name,
              email: options.email,
              password: options.password,
              position: options.position,
              account_id: parseInt(accountId),
              permissions: options.permissions,
              custom: options.custom,
              enable_reporting: this.enableReportingOnCreate,
              enable_hpp_receipts: this.enableHppReceiptsOnCreate
            }
          })

          if (result.data.hasOwnProperty('error') && !result.data.error) {
            Bus.$emit('openModal', {
              status: 'success',
              message: 'User Created Successfully',
              messageDetails: [
                `Name: ${options.name}`,
                `Email: ${options.email}`,
                `Password: ${options.password}`
              ],
              persist: true
            })

            // Redirect to the new user
            if (result.data.user_id) {
              this.$router.push({ name: 'User', params: { id: result.data.user_id } })
            }
          } else {
            throw new Error('Problem creating user')
          }
        } else {
          // Standard create/update
          options.account_id = accountId

          let result = await this.api({
            method: this.$route.params.id ? 'put' : 'post',
            url: '/users' + (this.$route.params.id ? '/' + this.$route.params.id : ''),
            data: options
          })

          if (result.data.hasOwnProperty('error') && !result.data.error) {
            Bus.$emit('openModal', {
              status: 'success',
              message: 'User Saved'
            })
          } else {
            throw new Error('Problem saving user')
          }
        }
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: (err.response && err.response.data && err.response.data.error) || err.message || err
        })
      } finally {
        this.isProcessing = false
        Bus.$emit('closeWaitModal')
      }
    }
  }
}
</script>

<style lang="less">
  .form__input--modified .v-input__slot {
    border: 1px solid var(--v-primary-base);
  }

  .custom-json-preview {
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 12px;
    font-size: 12px;
    line-height: 1.5;
    overflow-x: auto;
    white-space: pre;
  }
</style>



// WEBPACK FOOTER //
// src/components/manage/UserEdit.vue